// Vendor endpoints (VendorController). Vendors are LOCATION-SCOPED: each vendor belongs to
// exactly one operation unit and can never be paid outside it — the same real-world vendor
// serving two locations has two separate entries. The api* actions read that operation unit
// from JWT claims (not a body param), so the Vendors page mints a per-location CONTEXT token
// via /api/account/selectLeOu and uses it as the bearer for the list/save calls below. That
// minted token lives in PAGE state only — the app's own auth token is never replaced.

import { apiFetch, ApiError, apiUploadFileWithProgress } from "@/lib/api";

// ---------------------------------------------------------------------------
// Types (VendorDtoService.asMap / bankAccountAsMap)
// ---------------------------------------------------------------------------

export interface VendorBankAccountMap {
  id: number;
  bank: { id?: number | null; name?: string | null } | null;
  bankAccount: string | null;
  bankBranch: string | null;
  ifscCode: string | null;
  accountHolderName: string | null;
  verificationProofFileName: string | null;
  bankAcVerified: boolean;
  isDefault: boolean;
  /** Formula: verified AND proof present — the badge the payment side cares about. */
  verifiedForPayment: boolean;
}

/** One vendor (VendorDtoService.asMap). Legacy single-account fields (bankAc etc.) are
 *  mirrored from the default bank account and intentionally not used by this app. */
export interface VendorMap {
  id: number;
  name: string | null;
  address: string | null;
  contactPerson: string | null;
  contactNumber: string | null;
  email: string | null;
  emailVerified: boolean;
  whatsAppNumber: string | null;
  whatsAppVerified: boolean;
  verifiedForPayment: boolean;
  pan: string | null;
  /** NOT currently emitted by VendorDtoService.asMap — optional so the badge appears
   *  automatically if the backend starts sending it. */
  panVerified?: boolean | null;
  panActive?: boolean | null;
  operationUnit?: { id?: number | null; name?: string | null; shortName?: string | null } | null;
  bankAccounts: VendorBankAccountMap[] | null;
  /** First (default-ordered) bank account, or null. */
  defaultBankAccount: VendorBankAccountMap | null;
}

export interface VendorsPage {
  list: VendorMap[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
}

// ---------------------------------------------------------------------------
// Context token
// ---------------------------------------------------------------------------

/**
 * Mint a location-scoped context token: POST /api/account/selectLeOu with the NORMAL auth
 * token → a new JWT carrying {legalEntityId, operationUnitId} claims. 403 = the user has
 * no access to that location.
 */
export async function mintVendorContextToken(
  token: string,
  legalEntityId: number,
  operationUnitId: number,
  signal?: AbortSignal
): Promise<string> {
  const d = await apiFetch<{ access_token?: string }>("/api/account/selectLeOu", {
    token,
    method: "POST",
    body: { legalEntityId, operationUnitId },
    signal,
  });
  if (!d?.access_token) {
    throw new ApiError("The server did not return a context token.", 0);
  }
  return d.access_token;
}

// ---------------------------------------------------------------------------
// List / save
// ---------------------------------------------------------------------------

/** Paged vendor list of the context token's operation unit. Search is server-side
 *  whole-phrase over name/address/contact/email/PAN/bank fields. 403 = no vendor-list grant. */
export function fetchVendors(
  contextToken: string,
  params: { search?: string | null; currentPage: number; pageSize?: number },
  signal?: AbortSignal
): Promise<VendorsPage> {
  const search = params.search?.trim();
  return apiFetch<VendorsPage>("/api/vendor/apiRefreshList", {
    token: contextToken,
    method: "POST",
    body: {
      filterParams: search ? { search } : {},
      currentPage: params.currentPage,
      pageSize: params.pageSize ?? 20,
    },
    signal,
  });
}

export interface SaveVendorBankAccountPayload {
  /** Existing MstVendorBankAccount id (edit) — echo it back or the server soft-deletes the row. */
  id?: number;
  bank: { id: number };
  bankAccount: string;
  bankBranch?: string;
  ifscCode: string;
  accountHolderName?: string;
  /** Uploaded proof document — REQUIRED server-side for every non-empty row. */
  verificationProofFileName: string;
  /** Exactly one row true (the server makes the first row default when none is marked). */
  isDefault: boolean;
}

export interface SaveVendorPayload {
  id?: number; // edit
  name: string;
  address?: string;
  contactPerson?: string;
  contactNumber?: string;
  email?: string;
  whatsAppNumber?: string;
  pan?: string;
  /** Legacy header-level bank — kept in sync with the default account's bank. */
  bank?: { id: number } | null;
  bankAccounts: SaveVendorBankAccountPayload[];
}

export interface SaveVendorResult {
  myMessage?: string;
  errorMessage?: string;
  errors?: string;
  vendor?: VendorMap;
}

/** Create/update a vendor in the context token's operation unit. 403 = no make-payment-request
 *  grant; editing a vendor of another operation unit is rejected with errorMessage. */
export function saveVendor(contextToken: string, payload: SaveVendorPayload): Promise<SaveVendorResult> {
  return apiFetch<SaveVendorResult>("/api/vendor/apiSave", {
    token: contextToken,
    method: "POST",
    body: payload,
  });
}

// ---------------------------------------------------------------------------
// Bank-proof upload
// ---------------------------------------------------------------------------

export interface UploadVendorBankProofResult {
  fileName?: string;
  myMessage?: string;
  errorMessage?: string;
}

/**
 * Multipart upload of a bank-account proof document (field "filePath"; jpg/jpeg/png/pdf/doc/docx,
 * size-capped server-side). Uses the NORMAL auth token — the endpoint is not context-scoped.
 * May 403 for users without the vendor-maintenance/make grants.
 */
export function uploadVendorBankProof(
  token: string,
  file: File,
  onProgress?: (percent: number | null) => void
): Promise<UploadVendorBankProofResult> {
  return apiUploadFileWithProgress<UploadVendorBankProofResult>(
    "/api/vendor/uploadVendorBankProofFile",
    token,
    file,
    onProgress,
    "filePath"
  );
}
